import {
    useFloating,
    useClick,
    useDismiss,
    useRole,
    useInteractions,
    useListNavigation,
    FloatingFocusManager,
    offset,
    flip,
    size,
    autoUpdate,
} from '@floating-ui/react';
import PropTypes from 'prop-types';
import { AnimatePresence, motion } from 'framer-motion';
import DropdownContext from './DropdownContext';
import { cn } from 'Utils/cn';

export default function Dropdown(props) {
    const {
        children,
        trigger,
        open,
        onOpenChange,
        className,
        placement = 'bottom-start',
        listRef,
        activeIndex,
        onNavigate,
        virtual = false,
        onFloatingKeyDown,
    } = props;

    const { refs, floatingStyles, context, strategy, x, y } = useFloating({
        open,
        onOpenChange,
        whileElementsMounted: autoUpdate,
        placement,
        middleware: [
            offset(4),
            flip(),
            size({
                apply({ rects, elements, availableHeight }) {
                    Object.assign(elements.floating.style, {
                        minWidth: `${rects.reference.width}px`,
                        maxHeight: `${Math.min(availableHeight - 20, 520)}px`,
                    });
                },
            }),
        ],
    });

    const interactions = [useClick(context), useRole(context, { role: 'listbox' }), useDismiss(context)];

    if (listRef && onNavigate) {
        interactions.push(
            useListNavigation(context, {
                listRef,
                activeIndex,
                onNavigate,
                virtual,
                loop: true,
                focusItemOnOpen: true,
            }),
        );
    }

    const { getFloatingProps, getReferenceProps, getItemProps } = useInteractions(interactions);

    return (
        <DropdownContext.Provider value={{ refs, getReferenceProps, getItemProps }}>
            <div className={cn('relative', className)}>
                {trigger}

                <AnimatePresence>
                    {open && (
                        <FloatingFocusManager context={context}>
                            <motion.div
                                ref={refs.setFloating}
                                style={{
                                    ...floatingStyles,
                                    position: strategy,
                                    top: y ?? 0,
                                    left: x ?? 0,
                                }}
                                className="mt-1 w-full overflow-x-hidden overflow-y-auto scrollbar-thin rounded-md bg-white shadow-sm ring-1 ring-black/5 focus:outline-none text-sm z-dropdown min-w-[180px]"
                                tabIndex={-1}
                                {...getFloatingProps(onFloatingKeyDown ? { onKeyDown: onFloatingKeyDown } : undefined)}
                                initial={{ opacity: 0, y: -8 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -8 }}
                                transition={{ duration: 0.15, ease: 'easeOut' }}
                            >
                                {children}
                            </motion.div>
                        </FloatingFocusManager>
                    )}
                </AnimatePresence>
            </div>
        </DropdownContext.Provider>
    );
}

Dropdown.propTypes = {
    children: PropTypes.node.isRequired,
    trigger: PropTypes.node.isRequired,
    open: PropTypes.bool.isRequired,
    onOpenChange: PropTypes.func.isRequired,
    className: PropTypes.string,
    placement: PropTypes.string,
    listRef: PropTypes.object,
    activeIndex: PropTypes.number,
    onNavigate: PropTypes.func,
    virtual: PropTypes.bool,
    onFloatingKeyDown: PropTypes.func,
};
